import React from "react";
import { AppButton } from "components/shared/AppButton";
import { NipperLogLine, formatNipperLog } from "utils/nipper/nipperLog";
import { notify } from "utils/notify";
import {
  Card,
  CardTitle,
  EmptyState,
  LogKind,
  LogLine,
  LogList,
  LogMeta,
  LogText,
  LogToolbar,
} from "styles/ecosaltUpgrade.styles";

const KIND_LABEL = { sent: "Sent", firmware: "Firmware", note: "Note" } as const;

async function copyLog(text: string) {
  await navigator.clipboard.writeText(text);
  notify.success("Serial log copied");
}

function downloadLog(text: string) {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `nipper-serial-log-${new Date().toISOString().slice(0, 10)}.txt`;
  link.click();
  URL.revokeObjectURL(url);
}

/** Serial transcript: commands we sent, frames the firmware returned, and local notes. */
function EcoSaltSerialLog({ lines }: { lines: NipperLogLine[] }) {
  const text = formatNipperLog(lines);
  const empty = lines.length === 0;

  return (
    <Card aria-labelledby="ecosalt-log-title">
      <LogToolbar>
        <CardTitle id="ecosalt-log-title">Serial log</CardTitle>
        <span>
          <AppButton variant="ghost" disabled={empty} onClick={() => void copyLog(text).catch(() => notify.error("Could not copy the serial log"))}>
            Copy
          </AppButton>{" "}
          <AppButton variant="ghost" disabled={empty} onClick={() => downloadLog(text)}>
            Download
          </AppButton>
        </span>
      </LogToolbar>
      {empty ? (
        <EmptyState>No activity yet. Sent commands and firmware replies will show up here.</EmptyState>
      ) : (
        <LogList aria-label="Serial log">
          {lines.map((line, index) => (
            <LogLine key={`${line.at}-${index}`} $kind={line.kind}>
              <LogMeta>{line.at}</LogMeta>
              <LogKind $kind={line.kind}>{KIND_LABEL[line.kind]}</LogKind>
              <LogText>{line.text}</LogText>
            </LogLine>
          ))}
        </LogList>
      )}
    </Card>
  );
}

export default EcoSaltSerialLog;
